/**
 * @fileoverview AutoScaleScreenContainer - Responsive auto-scaling wrapper
 * @module screen-view-v2/components/shared/AutoScaleScreenContainer
 *
 * Wraps content and scales it to fit the available container space
 * using CSS transform + ResizeObserver.
 */

import React, { useRef, memo } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useScreenScale } from '../../../hooks/useScreenScale';
import type { UseScreenScaleOptions } from '../../../hooks/useScreenScale';
import { SCREEN_DESIGN_SIZE } from '../../../constants/designSize';
import styles from './AutoScaleScreenContainer.module.css';

export interface AutoScaleScreenContainerProps {
  children: ReactNode;
  scaleOptions?: UseScreenScaleOptions;
  className?: string;
  'data-testid'?: string;
  transformOrigin?: 'top center' | 'top left' | 'center center';
  showBeforeReady?: boolean;
}

export const AutoScaleScreenContainer = memo(function AutoScaleScreenContainer({
  children,
  scaleOptions,
  className,
  'data-testid': testId,
  transformOrigin = 'top center',
  showBeforeReady = false,
}: AutoScaleScreenContainerProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scale, isReady } = useScreenScale(containerRef, scaleOptions);

  const innerStyle: CSSProperties = {
    width: scaleOptions?.contentWidth ?? SCREEN_DESIGN_SIZE.WIDTH,
    height: scaleOptions?.contentHeight ?? SCREEN_DESIGN_SIZE.HEIGHT,
    transform: `scale(${scale})`,
    transformOrigin,
    visibility: isReady || showBeforeReady ? 'visible' : 'hidden',
  };

  return (
    <div
      ref={containerRef}
      className={className ? `${styles.container} ${className}` : styles.container}
      data-testid={testId}
    >
      <div className={styles.scaleWrapper} style={innerStyle}>
        {children}
      </div>
    </div>
  );
});

AutoScaleScreenContainer.displayName = 'AutoScaleScreenContainer';
